import React, { useEffect, useMemo, useState } from 'react';
import { Center, Row } from '@shared/ui/atoms/StructuralLayout';
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import { BodyRegular, BodySmall } from '@shared/ui/atoms/fonts/Body';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import Icon, { IconTypes } from '@shared/ui/atoms/icon/Icon';
import Layout from '@shared/ui/organisms/layout/Layout';
import Colours from '@shared/ui/atoms/Colours';
import styled from "styled-components";
import { desktopSmall, desktopWide, tablet } from '@shared/ui/atoms/MediaQueries';
import { HeadlineMedium, HeadlineSmall } from '@shared/ui/atoms/fonts/Headline';
import DateFlicker, { DateScale } from '@shared/ui/molecules/DateFlicker';
import { FlatContentCard } from '@shared/ui/atoms/FlatContentCard';
import { Trans, useTranslation } from "react-i18next";
import moment from "moment";
import { useSelector } from "react-redux";
import { selectConfig } from '@shared/store/config.duck';
import { selectCalendarDate } from '@shared/store/dashboard.duck';
import { SortableTable } from '@shared/ui/molecules/SortableTable';
import { agent } from '@shared/services/agent';
import { NameContentCell } from "./HolidayCarryOverPreview";

interface UsersRollOverExpiryPreview {
  year: number;
  expiryDate: string;
  userId: String;
  name: String;
  daysUsed: number;
  daysUsedUntilExpiry: number;
  basicAllowance: number;
  additionalDays: number;
  daysRolledOver: number;
  total: number;
}

function ContentCell(props: any) {
  const {item} = props;
  const usedExpiryDays = item?.daysUsedUntilExpiry < item?.daysRolledOver;
  return <BodySmall weight={usedExpiryDays ? 600 : 400} colour={props.showAlert && usedExpiryDays ? Colours.alertRed : Colours.black}>{props.value} {props.postfix}</BodySmall>
}

export function HolidayCarryOverExpiryPreview(props: Props) {
  document.body.style.backgroundColor = Colours.veryLightGrey;
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<UsersRollOverExpiryPreview[]>([]);
  const config = useSelector(selectConfig);
  const calendarDate = useSelector(selectCalendarDate);
  const {t} = useTranslation();

  const holidayStartYear = config.getHolidayYearStartDate(calendarDate) ?? moment();
  const holidayYearEndDate = config.getHolidayYearEndDate(calendarDate) ?? holidayStartYear.add(1, 'year');


  const dateRangeString = useMemo(() => {
    const start = holidayStartYear.format('MMMM YYYY');
    const end = holidayYearEndDate.format('MMMM YYYY');
    return `${start} - ${end}`;
  }, [holidayStartYear, holidayYearEndDate]);

  const carryOverExpiryDate = useMemo(() => {
    if (config.autoCarryOverExpiryDay && config.autoCarryOverExpiryMonth) {
      return moment()
        .year(holidayStartYear.year())
        .month(config.autoCarryOverExpiryMonth - 1)
        .date(config.autoCarryOverExpiryDay)
        .format('D MMM YYYY')
    }
    return '';
  }, [config.autoCarryOverExpiryMonth, config.autoCarryOverExpiryDay, holidayStartYear]);

  useEffect(() => {
    setLoading(true);
    try {
      agent.requests.get(`/api/v2/expiry-summary?year=${calendarDate.year()}`)
        .then((result: any) => setData(result?.data ?? []))
    } finally {
      setLoading(false);
    }
  }, [calendarDate]);

  const columns = useMemo(() => {
    const year = holidayStartYear.year();
    return [
      {header: 'Name', accessorKey: 'name', cell: (info: any) => <NameContentCell value={info} /> },
      {header: `Days used as of Expiry (${carryOverExpiryDate})`, accessorKey: 'daysUsedUntilExpiry', cell: (info: any) => <ContentCell showAlert={true} value={info.getValue()} postfix={'days'} item={data.find((r: UsersRollOverExpiryPreview) => r.userId === info.row.original.userId)} />},
      {header: `Total days used`, accessorKey: 'daysUsed', cell: (info: any) => <ContentCell showAlert={false} value={info.getValue()} postfix={'days'} item={data.find((r: UsersRollOverExpiryPreview) => r.userId === info.row.original.userId)} />},
      {header: `Total for ${year} `, enableSorting: false, accessorKey: 'total', cell: (info: any) => <ContentCell showAlert={false} value={info.getValue()} postfix={'days'} item={data.find((r: UsersRollOverExpiryPreview) => r.userId === info.row.original.userId)} />},
    ];
  }, [carryOverExpiryDate, data, holidayStartYear]);

  return (
    <>
      <Layout fullWidth={true} hideFooter={true}>

        {loading ? <Center><LoadingSpinner style={{marginTop: 80}} hideBorder={true} message={`This can take several minutes`} /></Center> : <>

          <RibbonContainer>
            <RibbonContentContainer>
              <BodyRegular weight={600}>Showing all users</BodyRegular>
              <div>
                <OutlineButton link={'/manage/holidays'} icon={IconTypes.Settings} text={'settings-page.holiday'} />
              </div>
            </RibbonContentContainer>
          </RibbonContainer>

          <ContentContainer>
            <HeaderRow>
              <HeadlineMedium color={Colours.alertRed}>{t('allowance.expiry-report')}</HeadlineMedium>
              <DateHeader>
                <HeadlineSmall>{dateRangeString}</HeadlineSmall>
                <DateFlicker className={'userSectionHeader__date'}
                             date={holidayStartYear}
                             hideDateText={true}
                             dateScale={DateScale.Year}
                             disabled={loading}
                             updateGlobalDate={true} />
              </DateHeader>
            </HeaderRow>
            <InformationRow>
              <FlatContentCard className={'holidayCarryOver__info'}>
                <Row>
                  <Icon icon={IconTypes.Alert} />
                  <BodySmall weight={400}><Trans i18nKey="allowance.holiday-carry-over-info.expiry-info" /></BodySmall>
                </Row>
              </FlatContentCard>
              <div className={'holidayCarryOver__infoContainer'}>
                <div className={'holidayCarryOver__infoLink'}>
                  <BodySmall weight={600} colour={Colours.alertRed}>{!carryOverExpiryDate ? t('allowance.holiday-carry-over-info.carry-over-does-not-expire') : t('allowance.holiday-carry-over-info.carry-over-expires', {carryOverExpiryDate: carryOverExpiryDate})}</BodySmall>
                  <BodySmall weight={400}><Trans i18nKey={'allowance.holiday-carry-over-info.leave-settings'} components={[<a href="/manage/holidays">Leave settings</a>]} /></BodySmall>
                </div>
              </div>
            </InformationRow>
            <SortableTable data={data}
                           columns={columns} />
          </ContentContainer>
        </>}
      </Layout>
    </>
  )
}

interface Props {
}


const ContentContainer = styled.div`
  max-width: 1400px;
  background-color: white;
  padding: 48px 64px;
  margin: 24px auto 0 auto;

  @media (${tablet}) {
     padding: 48px 64px;
     border-radius: 24px;
   }
  
`

const RibbonContainer = styled.div`
  background-color: white;
  box-shadow: 0 9px 22px 0 rgba(46, 74, 148, 0.08);
`

const RibbonContentContainer = styled.div`
  width: 100%;
  margin: 0 auto;
  max-width: 1400px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
`


const InformationRow = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 32px;
  .holidayCarryOver__info {
    max-width: 550px;
    padding: 24px 32px;
    i {
      margin-right: 10px;
      margin-top: 4px;
    }
  }
  .holidayCarryOver__infoContainer {
    display: flex;
    width: 100%;
    align-items: center;
    margin-top: 16px;
  }

  @media (${tablet}) {
    .holidayCarryOver__infoLink {
      margin-left: 16px;
    }
  }
  @media (${desktopSmall}) {
    flex-direction: row;
    .holidayCarryOver__infoContainer {
      margin-top: 0;
    }
  }
  @media (${desktopWide}) {
    .holidayCarryOver__infoLink {
      margin-left: auto;
    }
  }
`

const DateHeader = styled.div`
  display: flex;
  align-items: center;
`

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  .weekFlicker {
    width: 100px;
  }
  .linkButton {
    margin: 0;
    width: unset;
    max-width: unset;
  }
`
